/**
 * Proposals Page
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 6: Create ProposalsPage with filters
 *
 * Lists governance proposals with filtering capabilities.
 * Integrates ProposalFilters and ProposalCard components.
 *
 * @see AC-4.2.8.1 - Filter by status shows matching proposals
 * @see AC-4.2.8.2 - Already Voted badge shown when user has voted
 * @see AC-4.2.8.3 - Needs my vote filter shows actionable proposals
 *
 * @module pages/Governance/ProposalsPage
 */

import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import {
  ProposalFilters,
  ProposalCard,
  SemanticSearchBar,
  type FilterStatus,
} from '../../components/governance';
import type { SearchResult } from '../../services/searchService';
import {
  getProposals,
  isGovernanceError,
  type ProposalListItem,
} from '../../services/governanceService';
import { getCommentCounts } from '../../services/proposalCommentService';

// =============================================================================
// Component
// =============================================================================

/**
 * Proposals page with filtering
 */
export function ProposalsPage() {
  // Filter state
  const [status, setStatus] = useState<FilterStatus>('all');
  const [needsMyVote, setNeedsMyVote] = useState(false);

  // Data state
  const [proposals, setProposals] = useState<ProposalListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentCounts, setCommentCounts] = useState<Record<number, number>>({});

  // BL-045.3: Semantic search state
  const [semanticResults, setSemanticResults] = useState<SearchResult[] | null>(null);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const loadProposals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getProposals({
        status: status !== 'all' ? status : undefined,
        needsMyVote,
      });
      setProposals(data);
      setCommentCounts({}); // Clear stale counts before fetching fresh ones

      // BL-460: one batched request instead of N parallel fetches. The old
      // pattern was firing 20 simultaneous /comments/count calls per page
      // load; under shard-parallel test load this caused pg-pool
      // saturation on oracle-bridge that cascaded into /api/auth/session
      // queueing for unrelated tests. Single batched call uses one
      // backend connection and one DB round-trip. Still fire-and-forget
      // — comment-count badges are non-critical UI and shouldn't block
      // the proposal-card render.
      void getCommentCounts(data.map((p) => p.id)).then(setCommentCounts);
    } catch (e) {
      // Check if it's a real governance error (not network)
      if (isGovernanceError(e) && e.type !== 'network_error') {
        setError(e.message);
        setIsLoading(false);
        return;
      }

      // No data available when API is not reachable
      setProposals([]);
    } finally {
      setIsLoading(false);
    }
  }, [status, needsMyVote]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <ErrorBoundary>
      <div data-testid="proposals-page">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            Governance Proposals
          </h2>
          <p className="text-sm text-muted-foreground">
            Browse and vote on DAO proposals
          </p>
        </div>

        {/* BL-045.3: Semantic Search Bar */}
        <SemanticSearchBar
          onResults={(results) => {
            setSemanticResults(results);
          }}
          onClear={() => {
            setSemanticResults(null);
          }}
        />

        {/* Filters (hidden when semantic search is active) */}
        {!semanticResults && (
          <ProposalFilters
            status={status}
            needsMyVote={needsMyVote}
            onStatusChange={setStatus}
            onNeedsMyVoteChange={setNeedsMyVote}
          />
        )}

        {/* Content — BL-045.3: show semantic results when active, otherwise normal list */}
        {semanticResults ? (
          // Semantic search results mode
          semanticResults.length === 0 ? (
            <div className="text-center py-12" data-testid="proposals-empty">
              <p className="text-muted-foreground">
                No matching proposals found
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="semantic-results-list">
              {semanticResults.map((result) => (
                <a
                  key={`search-${result.id}`}
                  href={result.url || `/proposals/${result.id}`}
                  className="block p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                  data-testid="semantic-result-card"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      Proposal
                    </span>
                    {result.score !== null && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {Math.round(result.score * 100)}% match
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-foreground">{result.title}</h3>
                  {result.excerpt && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {result.excerpt.length > 150
                        ? result.excerpt.slice(0, 150) + '...'
                        : result.excerpt}
                    </p>
                  )}
                </a>
              ))}
            </div>
          )
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12" data-testid="proposals-loading">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="py-8" data-testid="proposals-error">
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12" data-testid="proposals-empty">
            <p className="text-muted-foreground">
              No proposals yet
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="proposals-list">
            {proposals.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                id={proposal.id}
                title={proposal.title}
                category={proposal.category}
                status={proposal.status as 'Active' | 'Pending' | 'Closed' | 'Passed' | 'Rejected' | 'Failed'}
                votingEndsAt={proposal.voting_ends_at}
                yesVotes={proposal.yes_votes}
                noVotes={proposal.no_votes}
                abstainVotes={proposal.abstain_votes}
                needsRefinementVotes={proposal.needs_refinement_votes}
                totalVotes={proposal.total_votes}
                quorumThreshold={proposal.quorum_threshold}
                userHasVoted={proposal.user_has_voted}
                commentCount={commentCounts[proposal.id] ?? 0}
              />
            ))}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default ProposalsPage;
